/**
 * SB6-ANA-CACHE-01 — canonical cache key derivation.
 *
 * In production, full-res images come from Azure Blob Storage via pre-signed
 * SAS URLs whose query string (the signature) rotates roughly every 15 min and
 * on every list re-fetch. The browser's HTTP cache keys on the *whole* signed
 * URL, so each re-mint produces a new cache key and the previously downloaded
 * bytes become unreachable even though they're still on disk.
 *
 * Our Cache Storage layer (`imageCache.ts`) keys instead on the **stable blob
 * path** — origin-less, query-less. That single rule is what lets the cache
 * survive SAS rotation and browser restarts (story §2.1-A).
 *
 *   Azure SAS:  https://media.windplatform.com/windplatform-acme/data-acquisitions/
 *                 <uuid>/full_res/IMG.jpg?sv=2023-…&sig=ABC…
 *               → windplatform-acme/data-acquisitions/<uuid>/full_res/IMG.jpg
 *
 *   dev-api:    http://localhost:3100/test_data/<uuid>/full_res/IMG.jpg
 *               → test_data/<uuid>/full_res/IMG.jpg   (no query string)
 *
 * The derivation deliberately drops BOTH origin and query so the same blob
 * served from a re-minted SAS URL, a different CDN host, or the local dev-api
 * collapses onto one stable key.
 */

/**
 * Derive a stable, origin-less, query-less cache key from an image URL.
 *
 * - Strips the query string (SAS signature, cache-busters, anything).
 * - Strips the origin (scheme + host + port) so a CDN host change or a
 *   localhost dev-api host doesn't fork the key.
 * - Leaves the path intact, with the leading slash removed and the path
 *   segments percent-decoded where safe, so two encodings of the same blob
 *   name still collide.
 *
 * Falls back to a best-effort manual parse for inputs the URL constructor
 * can't handle (it never throws — a bad URL still yields *some* stable key so
 * the cache never crashes the Viewer).
 */
export function canonicalKey(url: string): string {
  if (typeof url !== "string" || url.length === 0) return "";

  // data:/blob: URLs are already self-contained, content-addressed handles —
  // there's no stable blob path to key on, and they should never be cached
  // through this layer. Return them verbatim so callers can detect + skip.
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;

  let pathname: string;
  try {
    // `base` lets relative URLs (e.g. "/api/files/…") parse too; the base
    // origin is then stripped along with every other origin below.
    const parsed = new URL(url, "https://canonical.base.invalid");
    pathname = parsed.pathname;
  } catch {
    // Manual fallback: cut the query/hash, then strip a leading scheme://host.
    const noQuery = url.split(/[?#]/, 1)[0] ?? url;
    const schemeStripped = noQuery.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");
    const slash = schemeStripped.indexOf("/");
    pathname = slash >= 0 ? schemeStripped.slice(slash) : `/${schemeStripped}`;
  }

  // Drop the leading slash so keys read as plain blob paths.
  let key = pathname.replace(/^\/+/, "");

  // Percent-decode the path so "%20"/"+"-style re-encodings of the same blob
  // name collapse onto one key. Decoding is best-effort: a malformed escape
  // leaves the segment as-is rather than throwing.
  try {
    key = decodeURIComponent(key);
  } catch {
    /* keep the raw (still query-less, still origin-less) key */
  }

  return key;
}
